import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Flex, Box } from '@cad-arc';
import { autorun } from 'mobx';
import { Panel } from '../../panels/panel';
import { WorkspacePanel } from '../../panels/workspace-panel';
import { PanelTitle } from '../../panels/panel-title';
import { LoadingIndicator } from '../../common/components/loading-indicator';
import { FederatedSearchUpdateToEvent } from '../update-to-event/federated-search-update-to-event';
import { useFederatedQueryIds } from '../../federated-search/federated-search-context';
import {
    FederatedQueriesFilters,
    FederatedSearchParsedResult,
} from 'mark43-federated-search/dist/lib/types';
import { EventAutocompleteComponent } from '../components/event-autocomplete';
import { useUpdateToEventKeyboardHandler } from './use-update-to-event-keyboard-handler';
import { AppName, AppNameProvider } from '@m43/core';
import { getUniversalSearchLabel } from '../../common/helpers/get-usx-labels';
import { useEnabledFeatures } from '../../common/hooks/enabled-features';
import { FederatedSearchPreviewRow } from './dex-next-results-preview-row';
import { useCadLinkedEntitiesByQueryId } from './use-cad-linked-entities-by-query-id';
import { getAgencyEventFromFederatedSearchQueryLinks, getFederatedQueryLinks } from './utils';
import { FederatedSearchMoreActionsMenu } from '../../federated-search/dex-next-results-more-actions-menu';
import { useReadAllDexQueries } from './use-select-dex-results';

const FederatedSearchResults = lazy(() =>
    import('federated-search/FederatedSearchClient').then((module) => ({
        default: module.FederatedSearchClient,
    }))
);

export function UpdateToEventWrapper(props: {
    queryId: string;
    queryResultId?: string;
    linkedEventId?: number;
    federatedSearchParsedResult?: FederatedSearchParsedResult;
}) {
    const { showEntitiesPopoverRef } = useUpdateToEventKeyboardHandler();
    return (
        <FederatedSearchUpdateToEvent
            key={props.queryResultId}
            queryId={props.queryId}
            queryResultId={props.queryResultId}
            linkedEventId={props.linkedEventId}
            federatedSearchParsedResult={props.federatedSearchParsedResult}
            showEntitiesPopoverRef={showEntitiesPopoverRef}
            EventAutocompleteComponent={EventAutocompleteComponent}
            showLeadingVisualIcon
        />
    );
}

export function DexNextResultsPanel({ panel }: { panel: WorkspacePanel }) {
    const federatedQueryIds = useFederatedQueryIds();
    const { dexNextEnabled } = useEnabledFeatures();
    const [selectedFederatedQueryId, setSelectedFederatedQueryId] = useState<string>('');

    const { mutate: cleanUnreadUsxQueries } = useReadAllDexQueries();

    const cleanUnreadQueries = useCallback(() => {
        if (!dexNextEnabled) {
            return;
        }
        cleanUnreadUsxQueries();
    }, [cleanUnreadUsxQueries, dexNextEnabled]);

    useEffect(() => {
        return autorun(() => {
            if (panel.isFocused) {
                cleanUnreadQueries();
            }
        });
    }, [cleanUnreadQueries, panel]);

    const filters: FederatedQueriesFilters = useMemo(() => {
        return {
            federatedQueryIds,
            initiatedBy: [],
        };
    }, [federatedQueryIds]);

    const {
        data: federatedSearchQueryLinkDtos,
        isLoading: isLinkedEntitiesLoading,
        isError: isLinkedEntitiesError,
    } = useCadLinkedEntitiesByQueryId(federatedQueryIds);

    const { agencyEventId } = useMemo(() => {
        const federatedQueryLinksForCurrentQueryId = getFederatedQueryLinks(
            federatedSearchQueryLinkDtos,
            selectedFederatedQueryId
        );
        return getAgencyEventFromFederatedSearchQueryLinks(federatedQueryLinksForCurrentQueryId);
    }, [selectedFederatedQueryId, federatedSearchQueryLinkDtos]);

    const panelTitleElement = (
        <PanelTitle>{`${getUniversalSearchLabel(dexNextEnabled)} Results`}</PanelTitle>
    );

    return (
        <Panel
            testId="DEX_NEXT_RESULTS_PANEL"
            panel={panel}
            header={
                <Box
                    display="flex"
                    justifyContent="space-between"
                    flexGrow={1}
                    alignItems="center"
                    alignSelf="center"
                >
                    {panelTitleElement}
                    <FederatedSearchMoreActionsMenu />
                </Box>
            }
            body={
                <Suspense fallback={<LoadingIndicator />}>
                    <AppNameProvider appName={AppName.CAD_NEXT}>
                        <Flex flexGrow={1} h={0}>
                            <FederatedSearchResults
                                applicationScope="CAD_DISPATCH"
                                federatedQueriesFilters={filters}
                                componentsProps={{
                                    FederatedQueryResultDetails: {
                                        renderButtons: (props) => {
                                            const queryId = props.activeQueryResult?.queryId;
                                            const queryResultId = props.activeQueryResult?.id;
                                            const federatedSearchParsedResult =
                                                props.parsedResultObject;
                                            return queryId ? (
                                                <UpdateToEventWrapper
                                                    queryId={queryId}
                                                    queryResultId={queryResultId}
                                                    linkedEventId={agencyEventId}
                                                    federatedSearchParsedResult={
                                                        federatedSearchParsedResult
                                                    }
                                                />
                                            ) : null;
                                        },
                                    },
                                    FederatedSearchResult: {
                                        onQuerySelect: (federatedQueryId: string) => {
                                            setSelectedFederatedQueryId(federatedQueryId);
                                        },
                                        renderPreviewSummary: ({
                                            previewSummary,
                                            federatedQuery,
                                            isWarning,
                                            isDanger,
                                        }) => (
                                            <FederatedSearchPreviewRow
                                                previewSummary={previewSummary}
                                                federatedQuery={federatedQuery}
                                                federatedSearchQueryLinkDtos={
                                                    federatedSearchQueryLinkDtos
                                                }
                                                isLinkedEntitiesLoading={isLinkedEntitiesLoading}
                                                isLinkedEntitiesError={isLinkedEntitiesError}
                                                isWarning={isWarning}
                                                isDanger={isDanger}
                                            />
                                        ),
                                    },
                                }}
                            />
                        </Flex>
                    </AppNameProvider>
                </Suspense>
            }
        />
    );
}
